/**
 * My Docs picker — API client (Send page "My Docs" source and the FAR rule folder picker).
 * Backend: sendlinxapi FileController@browse / FileController@resolve, and the existing
 * UserController@shareFiles to send the picked files without uploading them again.
 *
 * Set REACT_APP_MYDOCS_MOCK=true to run the picker on in-memory sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const MY_DOCS_ROOT = "/My Docs";

export const isMyDocsMockMode = () => {
    try{ return process.env.REACT_APP_MYDOCS_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/** "/My Docs/A/b.pdf" -> "/My Docs/A" */
export const parentFolder = (path) => {
    const value = String(path || "");
    const index = value.lastIndexOf("/");
    return index > 0 ? value.substring(0, index) : MY_DOCS_ROOT;
};

/**
 * Deepest folder that contains every picked item. Sent as `path` to shareFiles so the
 * zip keeps the folder structure relative to that folder.
 */
export const commonFolder = (items) => {
    const folders = (items || []).map(item => parentFolder(item.path).split("/").filter(Boolean));
    if(folders.length <= 0){
        return MY_DOCS_ROOT;
    }
    const common = [];
    for(let i = 0; i < folders[0].length; i++){
        const part = folders[0][i];
        if(folders.every(parts => parts[i] === part)){
            common.push(part);
        }else{
            break;
        }
    }
    return common.length > 0 ? "/"+common.join("/") : MY_DOCS_ROOT;
};

/* ---------------- mock store ---------------- */
const mockTree = [
    { id: 10, name: "Customers", type: "folder", path: "/My Docs/Customers" },
    { id: 11, name: "ABC Inc", type: "folder", path: "/My Docs/Customers/ABC Inc" },
    { id: 12, name: "Invoices", type: "folder", path: "/My Docs/Customers/ABC Inc/Invoices" },
    { id: 13, name: "Inv-637_abc.pdf", type: "file", size: 254000, path: "/My Docs/Customers/ABC Inc/Invoices/Inv-637_abc.pdf" },
    { id: 14, name: "Inv-640_abc.pdf", type: "file", size: 198000, path: "/My Docs/Customers/ABC Inc/Invoices/Inv-640_abc.pdf" },
    { id: 15, name: "Contract.docx", type: "file", size: 88000, path: "/My Docs/Customers/ABC Inc/Contract.docx" },
    { id: 16, name: "XYZ Corp", type: "folder", path: "/My Docs/Customers/XYZ Corp" },
    { id: 17, name: "Quote-2026.pdf", type: "file", size: 120000, path: "/My Docs/Customers/XYZ Corp/Quote-2026.pdf" },
    { id: 18, name: "Reports", type: "folder", path: "/My Docs/Reports" },
    { id: 19, name: "stockreport_q3.xlsx", type: "file", size: 45000, path: "/My Docs/Reports/stockreport_q3.xlsx" },
    { id: 20, name: "Team photo.jpg", type: "file", size: 2400000, path: "/My Docs/Team photo.jpg" }
];
const delay = (v) => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(v))), 150));
const mockItem = (row) => {
    const files = row.type === "folder" ? mockTree.filter(r => r.type === "file" && r.path.indexOf(row.path+"/") === 0) : [row];
    const size = files.reduce((sum, f) => sum + f.size, 0);
    return { id: row.id, name: row.name, type: row.type, path: row.path, folder: parentFolder(row.path), size, modified: "10/19/2026 09:30 AM", extension: row.type === "file" ? row.name.split(".").pop() : "" };
};

const MyDocsApi = {
    /** One level of a My Docs folder (or a name search below it). Folders first. */
    browse(path = MY_DOCS_ROOT, search = "", source = null){
        if(isMyDocsMockMode()){
            const q = String(search || "").trim().toLowerCase();
            const rows = mockTree.filter(r => q
                ? (r.path.indexOf(path+"/") === 0 && r.name.toLowerCase().indexOf(q) >= 0)
                : parentFolder(r.path) === path);
            rows.sort((a, b) => (a.type !== b.type ? (a.type === "folder" ? -1 : 1) : a.name.localeCompare(b.name)));
            return delay({ path, search: q, items: rows.map(mockItem), truncated: false });
        }
        const params = { path };
        if(search){ params.search = search; }
        return request("GET", "user/files/browse", { params, source });
    },
    /** Expands picked items into the files that will be sent (with each file's Sendlinx folder). */
    resolve(ids, source = null){
        if(isMyDocsMockMode()){
            const items = ids.map(id => mockTree.find(r => r.id === id)).filter(Boolean).map(row => {
                const base = mockItem(row);
                const files = row.type === "folder" ? mockTree.filter(r => r.type === "file" && r.path.indexOf(row.path+"/") === 0) : [row];
                return Object.assign(base, {
                    fileCount: files.length,
                    truncated: false,
                    files: files.map(f => ({ id: f.id, name: f.name, size: f.size, folder: parentFolder(f.path), path: f.path }))
                });
            });
            return delay({ items });
        }
        return request("POST", "user/files/resolve", { data: { ids }, source });
    },
    /**
     * Sends the picked My Docs files as a secure link (and emails it to recipients) without
     * uploading them again. Uses the existing shareFiles endpoint, which re-checks
     * File Attachment Rules on the server against each file's real My Docs folder.
     */
    share(requestData, source = null){
        if(isMyDocsMockMode()){
            return delay({ hash: "mockhash"+Date.now().toString(36) });
        }
        return request("POST", "user/files/share", { data: requestData, source });
    }
};

export default MyDocsApi;
